import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Enhanced Admin Authentication Hook
 * Implements secure admin-only authentication with comprehensive audit logging
 * and fail-secure principles throughout the authentication flow.
 */
export function useAdminAuth() {
  const [adminSession, setAdminSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const signInAdmin = useCallback(async (email, password) => {
    try {
      setError(null);
      
      // Capture request metadata for audit logging
      const requestMetadata = {
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        referrer: document.referrer,
        origin: window.location.origin
      };
      
      // Step 1: Authenticate with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (authError) {
        // Log failed authentication attempt
        await supabase.rpc('log_admin_attempt', {
          email,
          success: false,
          reason: authError.message,
          source_ip: 'client-side', // Server will capture real IP
          user_agent: requestMetadata.userAgent
        });
        
        throw authError;
      }
      
      // Step 2: Immediately verify admin status (fail secure)
      const { data: isAdmin, error: adminError } = await supabase
        .rpc('is_admin_user');
        
      if (adminError) {
        console.error('Admin verification failed:', adminError);
        
        // Log admin verification failure
        await supabase.rpc('log_admin_attempt', {
          email,
          success: false,
          reason: 'admin_verification_failed',
          source_ip: 'client-side',
          user_agent: requestMetadata.userAgent
        });
        
        // Sign them out immediately (fail secure)
        await supabase.auth.signOut();
        throw new Error('Unable to verify admin privileges');
      }
      
      if (!isAdmin) {
        // Log failed admin attempt - user exists but not admin
        await supabase.rpc('log_admin_attempt', {
          email,
          success: false,
          reason: 'insufficient_privileges',
          source_ip: 'client-side',
          user_agent: requestMetadata.userAgent
        });
        
        // Sign them out immediately (fail secure)
        await supabase.auth.signOut();
        throw new Error('Access denied: Administrator privileges required');
      }
      
      // Step 3: Log successful admin login
      await supabase.rpc('log_admin_attempt', {
        email,
        success: true,
        reason: 'successful_admin_login',
        source_ip: 'client-side',
        user_agent: requestMetadata.userAgent
      });
      
      setAdminSession(authData.session);
      console.log('✅ Admin authentication successful');
      
      return authData;
      
    } catch (err) {
      console.error('❌ Admin authentication failed:', err.message);
      setError(err.message);
      throw err;
    }
  }, []);
  
  const signOut = useCallback(async () => {
    try {
      const currentSession = await supabase.auth.getSession();
      
      if (currentSession.data.session) {
        // Log admin logout
        await supabase.rpc('log_admin_attempt', {
          email: currentSession.data.session.user.email,
          success: true,
          reason: 'admin_logout',
          source_ip: 'client-side',
          user_agent: navigator.userAgent
        });
      }
      
      await supabase.auth.signOut();
      setAdminSession(null);
      console.log('✅ Admin logout successful');
    } catch (error) {
      console.error('❌ Admin logout failed:', error);
      // Even if logging fails, still sign out
      await supabase.auth.signOut();
      setAdminSession(null);
    }
  }, []);
  
  const validateAdminSession = useCallback(async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session validation error:', sessionError);
        setAdminSession(null);
        return;
      }
      
      if (session) {
        // Re-verify admin status on session restore (fail secure)
        const { data: isAdmin, error: adminError } = await supabase.rpc('is_admin_user');
        
        if (adminError) {
          console.error('Admin verification failed during session validation:', adminError);
          await supabase.auth.signOut();
          setAdminSession(null);
          return;
        }
        
        if (isAdmin) {
          setAdminSession(session);
          console.log('✅ Admin session validated');
        } else {
          // User lost admin privileges - sign them out
          console.warn('⚠️ User no longer has admin privileges');
          
          // Log privilege loss
          await supabase.rpc('log_admin_attempt', {
            email: session.user.email,
            success: false,
            reason: 'admin_privileges_revoked',
            source_ip: 'client-side',
            user_agent: navigator.userAgent
          });
          
          await supabase.auth.signOut();
          setAdminSession(null);
        }
      } else {
        setAdminSession(null);
      }
    } catch (err) {
      console.error('Session validation failed:', err);
      // Fail secure: sign out on any validation error
      await supabase.auth.signOut();
      setAdminSession(null);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    validateAdminSession();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event);
        
        if (event === 'SIGNED_OUT') {
          setAdminSession(null);
        } else if (event === 'SIGNED_IN' && session) {
          // Verify admin status for any sign-in event
          const { data: isAdmin, error: adminError } = await supabase.rpc('is_admin_user');
          
          if (adminError || !isAdmin) {
            console.warn('⚠️ Non-admin user attempted sign-in');
            await supabase.auth.signOut();
            setAdminSession(null);
          } else {
            setAdminSession(session);
          }
        } else if (event === 'TOKEN_REFRESHED' && session) {
          // Re-verify admin status on token refresh
          const { data: isAdmin } = await supabase.rpc('is_admin_user');
          if (!isAdmin) {
            await supabase.auth.signOut();
            setAdminSession(null);
          }
        }
      }
    );
    
    return () => subscription.unsubscribe();
  }, [validateAdminSession]);
  
  return {
    adminSession,
    loading,
    error,
    signInAdmin,
    signOut,
    isAdmin: !!adminSession,
    validateAdminSession
  };
}
